import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bell } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

export default function NotificationBell() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);

  const load = () => {
    api.get("/notifications").then(({ data }) => {
      setItems(data.items || []);
      setUnread(data.unread || 0);
    }).catch(() => {});
  };

  useEffect(() => {
    if (!user) { setItems([]); setUnread(0); return; }
    load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, [user]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const markAll = async () => {
    try {
      await api.post("/notifications/read-all");
      setUnread(0);
      setItems((cur) => cur.map((n) => ({ ...n, read: true })));
    } catch { /* noop */ }
  };

  if (!user) return null;

  return (
    <div ref={ref} className="relative">
      <button
        data-testid="notif-bell"
        onClick={() => { setOpen((o) => !o); if (!open && unread) markAll(); }}
        className="relative p-2 text-muted-foreground hover:text-foreground transition-colors"
        aria-label="Notifications"
      >
        <Bell size={18} strokeWidth={1.5} />
        {unread > 0 && <span data-testid="notif-dot" className="bell-dot" />}
      </button>

      {open && (
        <div
          data-testid="notif-dropdown"
          /* On mobile: fixed to viewport, 12px gutter both sides — bulletproof against any header layout.
             On ≥sm: classic absolute dropdown below the bell, fixed width. */
          className="fixed sm:absolute top-14 sm:top-full left-3 right-3 sm:left-auto sm:right-0 sm:mt-3 w-auto sm:w-[360px] paper p-0 shadow-lg z-50"
        >
          <div className="flex items-center justify-between px-5 py-4 hairline-b">
            <div className="label">Notifications</div>
            <Link
              data-testid="notif-view-inbox"
              to="/inbox"
              onClick={() => setOpen(false)}
              className="mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground hover:text-foreground"
            >
              View inbox →
            </Link>
          </div>
          {items.length === 0 ? (
            <div className="px-5 py-10 text-center text-muted-foreground italic serif">
              No messages from the threshold yet.
            </div>
          ) : (
            <ul className="max-h-[420px] overflow-y-auto">
              {items.map((n) => (
                <li
                  key={n.id}
                  data-testid={`notif-${n.id}`}
                  className={`px-5 py-4 hairline-b last:border-b-0 ${n.read ? "opacity-70" : "bg-[hsl(var(--primary)/0.05)]"}`}
                >
                  <NotificationRow n={n} onNavigate={() => setOpen(false)} />
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function NotificationRow({ n, onNavigate }) {
  if (n.type === "share") {
    return (
      <Link to={`/concept/${n.payload?.concept_id}`} onClick={onNavigate} className="block">
        <div className="mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--primary))] mb-1">
          {n.payload?.from_name || "Someone"} shared
        </div>
        <div className="serif text-foreground leading-snug">{n.payload?.title || "a correlation"}</div>
        {n.payload?.message && <div className="text-sm text-muted-foreground italic mt-1">"{n.payload.message}"</div>}
      </Link>
    );
  }
  if (n.type === "recommendation") {
    return (
      <Link to={`/concept/${n.payload?.concept_id}`} onClick={onNavigate} className="block">
        <div className="mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--accent))] mb-1">
          Recommended for you
        </div>
        <div className="serif text-foreground leading-snug">{n.payload?.title}</div>
        {n.payload?.reason && <div className="text-sm text-muted-foreground mt-1">{n.payload.reason}</div>}
      </Link>
    );
  }
  if (n.type === "submission_approved") {
    return (
      <div>
        <div className="mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--primary))] mb-1">
          Your submission was admitted
        </div>
        <div className="serif text-foreground leading-snug">{n.payload?.title}</div>
      </div>
    );
  }
  if (n.type === "reply") {
    return (
      <Link to={`/concept/${n.payload?.concept_id}`} onClick={onNavigate} className="block">
        <div className="mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--primary))] mb-1">
          {n.payload?.from_name || "Someone"} replied
        </div>
        <div className="serif text-foreground leading-snug">on {n.payload?.concept_title || "a correlation"}</div>
        {n.payload?.snippet && <div className="text-sm text-muted-foreground italic mt-1">"{n.payload.snippet}"</div>}
      </Link>
    );
  }
  if (n.type === "feedback") {
    return (
      <Link to="/admin?tab=feedback" onClick={onNavigate} className="block">
        <div className="mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--accent))] mb-1">
          New {n.payload?.kind || "feedback"} from {n.payload?.from_name || "a reader"}
        </div>
        {n.payload?.snippet && <div className="text-sm text-muted-foreground italic mt-1">"{n.payload.snippet}"</div>}
      </Link>
    );
  }
  if (n.type === "follow") {
    const slug = n.payload?.from_handle || n.payload?.from_user_id;
    return (
      <Link to={slug ? `/u/${slug}` : "/inbox"} onClick={onNavigate} className="block">
        <div className="mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--primary))] mb-1">
          {n.payload?.from_name || "Someone"} followed you
        </div>
        {n.payload?.from_handle && (
          <div className="text-sm text-muted-foreground italic mt-1">@{n.payload.from_handle}</div>
        )}
      </Link>
    );
  }
  return <div className="serif text-foreground">{n.payload?.title || "Notification"}</div>;
}
